const DETAIL_LABELS = {
  requestId: 'Request ID',
  request_id: 'Request ID',
  supportGrantId: 'Support grant ID',
  support_grant_id: 'Support grant ID',
  reason: 'Reason',
  uiContext: 'UI context',
  ui_context: 'UI context',
  field: 'Field',
  email: 'Email',
  phone: 'Phone',
  profileUid: 'Profile UID',
  profile_uid: 'Profile UID',
  actorUid: 'Actor UID',
  actor_uid: 'Actor UID',
  action: 'Action',
  resultCount: 'Result count',
  result_count: 'Result count',
  page: 'Page',
  pageSize: 'Page size',
  page_size: 'Page size',
  filters: 'Filters',
};

const QUERY_FIELDS = [
  'actorUid',
  'profileUid',
  'action',
  'from',
  'to',
  'ip',
  'requestId',
  'supportGrantId',
];

function pickString(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function pickFirst(...values) {
  return values.find((value) => pickString(value) !== '');
}

function normalizeList(value) {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    return Object.values(value);
  }
  return [];
}

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isActionLogRowCandidate(value) {
  if (!isPlainObject(value)) {
    return false;
  }

  return [
    'created',
    'createdAt',
    'created_at',
    'action',
    'owner',
    'actorUid',
    'actor_uid',
    'profile',
    'profileUid',
    'profile_uid',
    'ip',
    'details',
  ].some((field) => Object.prototype.hasOwnProperty.call(value, field));
}

function findActionLogRows(response) {
  const explicitCandidates = [
    response?.actionLogs,
    response?.items,
    response?.data?.actionLogs,
    response?.data?.items,
  ];

  for (const candidate of explicitCandidates) {
    if (Array.isArray(candidate)) {
      return candidate.filter(isActionLogRowCandidate);
    }

    const rows = normalizeList(candidate).filter(isActionLogRowCandidate);
    if (rows.length > 0) {
      return rows;
    }
  }

  return normalizeList(response).filter(isActionLogRowCandidate);
}

function clampInteger(value, fallback, { min, max } = {}) {
  const number = Number.parseInt(value, 10);
  if (!Number.isFinite(number)) {
    return fallback;
  }

  const lowerBounded = min === undefined ? number : Math.max(min, number);
  return max === undefined ? lowerBounded : Math.min(max, lowerBounded);
}

function formatDateTime(value) {
  const rawValue = pickString(value);
  if (!rawValue) {
    return '-';
  }

  const date = new Date(rawValue);
  if (Number.isNaN(date.getTime())) {
    return rawValue;
  }

  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatLabel(key) {
  const normalizedKey = pickString(key);
  if (!normalizedKey) {
    return 'Detail';
  }

  if (DETAIL_LABELS[normalizedKey]) {
    return DETAIL_LABELS[normalizedKey];
  }

  return normalizedKey
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatDetailValue(value) {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  if (Array.isArray(value)) {
    const items = value
      .map((item) => (isPlainObject(item) ? Object.keys(item).slice(0, 4).join(', ') : pickString(item)))
      .filter(Boolean)
      .slice(0, 6);
    return items.join(', ');
  }

  if (isPlainObject(value)) {
    const keys = Object.keys(value).filter(Boolean).slice(0, 6);
    return keys.length ? `fields: ${keys.join(', ')}` : '';
  }

  return pickString(value);
}

function parseDetails(details) {
  if (isPlainObject(details)) {
    return details;
  }

  const text = pickString(details);
  if (!text) {
    return null;
  }

  try {
    const parsed = JSON.parse(text);
    return isPlainObject(parsed) ? parsed : text;
  } catch {
    return text;
  }
}

export function buildDetailsSummary(details) {
  const parsed = parseDetails(details);
  if (!parsed) {
    return '-';
  }

  if (!isPlainObject(parsed)) {
    return pickString(parsed) || '-';
  }

  const prioritizedKeys = [
    'requestId',
    'request_id',
    'supportGrantId',
    'support_grant_id',
    'reason',
    'uiContext',
    'ui_context',
    'field',
    'email',
    'phone',
  ];
  const keys = [
    ...prioritizedKeys.filter((key) => Object.prototype.hasOwnProperty.call(parsed, key)),
    ...Object.keys(parsed).filter((key) => !prioritizedKeys.includes(key)),
  ];

  const items = keys
    .map((key) => {
      const value = formatDetailValue(parsed[key]);
      return value ? `${formatLabel(key)}: ${value}` : '';
    })
    .filter(Boolean)
    .slice(0, 8);

  return items.length ? items.join('; ') : '-';
}

function formatPersonLabel(value) {
  if (!isPlainObject(value)) {
    return '-';
  }

  const firstName = pickString(pickFirst(value.firstName, value.first_name));
  const lastName = pickString(pickFirst(value.lastName, value.last_name));
  const fullName = pickString(pickFirst(value.fullName, value.full_name, value.name));
  const uid = pickString(pickFirst(value.uid, value.id));
  const name = [firstName, lastName].filter(Boolean).join(' ') || fullName;

  return name || uid || '-';
}

function resolvePersonLabel(value, fallbackUid) {
  const label = formatPersonLabel(value);
  return label === '-' ? (fallbackUid || '-') : label;
}

export function mapActionLogDto(item, index = 0) {
  const created = pickString(pickFirst(item?.created, item?.createdAt, item?.created_at));
  const action = pickString(item?.action) || '-';
  const actorUid = pickString(pickFirst(item?.owner?.uid, item?.owner?.id, item?.actorUid, item?.actor_uid));
  const profileUid = pickString(pickFirst(item?.profile?.uid, item?.profile?.id, item?.profileUid, item?.profile_uid));

  return {
    key: `${created || action || 'action-log'}-${actorUid || profileUid || index}-${index}`,
    created,
    createdLabel: formatDateTime(created),
    action,
    actorLabel: resolvePersonLabel(item?.owner, actorUid),
    actorUid,
    profileLabel: resolvePersonLabel(item?.profile, profileUid),
    profileUid,
    ip: pickString(item?.ip) || '-',
    detailsSummary: buildDetailsSummary(item?.details),
  };
}

export function mapActionLogSearchResponseToDto(response) {
  const source = findActionLogRows(response);
  const rows = source.map(mapActionLogDto);
  const total = clampInteger(response?.total, rows.length, { min: 0 });

  return {
    rows,
    total,
  };
}

export function buildActionLogQuery(filters = {}) {
  const query = {};

  QUERY_FIELDS.forEach((field) => {
    const value = pickString(filters[field]);
    if (value) {
      query[field] = value;
    }
  });

  query.order = filters.order === 'asc' ? 'asc' : 'desc';
  query.limit = clampInteger(filters.limit, 50, { min: 1, max: 100 });
  query.offset = clampInteger(filters.offset, 0, { min: 0 });

  return query;
}
